import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah8k8joh653ivfvnob0';

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('已收到你的订阅，直播预告会在重点场次开始前发送到你的邮箱。');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus('loading');
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('订阅成功，重点场次的直播预告会提前发送到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch (error) {
      setStatus('error');
      setMessage('网络异常，请检查网络后重试。');
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-notification-3-line text-lg" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">订阅直播预告提醒</h3>
          <p className="mt-1 text-sm leading-relaxed text-foreground-600">
            留下邮箱，我们会在当天重点场次开始前 30 分钟提醒你，热门场次不轻易错过。
          </p>
        </div>
      </div>

      <form
        id="live-reminder-subscribe"
        data-readdy-form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 sm:flex-row"
        noValidate
      >
        <label className="flex-1">
          <span className="sr-only">邮箱地址</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="请输入你的邮箱地址"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <input
          type="text"
          name="website_alt"
          className="subscribe-alt-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'loading' ? '提交中…' : '订阅提醒'}
        </button>
      </form>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-secondary-700">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" aria-hidden="true" />
          </span>
          {message}
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-accent-700">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line" aria-hidden="true" />
          </span>
          {message}
        </p>
      )}
    </div>
  );
}